"use client";

import { useEffect, useState } from "react";
import Script from "next/script";
import {
  clearPendingAnalyticsEvents,
  readPendingAnalyticsEvents,
} from "@/lib/analytics/client";
import { CookieConsent, hasCookieConsent } from "./cookie-consent";

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

const GTAG_ID = "G-QKDLD1WF7K";
const GTM_ID = "GTM-PRNGBNH2";

export function Analytics() {
  const initialState = hasCookieConsent();
  const [cookieAccepted, setCookieAccepted] = useState(initialState);
  const [analyticsReady, setAnalyticsReady] = useState(false);

  // Check for pending analytics events and fire them
  useEffect(() => {
    if (!analyticsReady || !cookieAccepted) {
      return;
    }

    // window.gtag MUST BE READY HERE
    const pendingEvents = readPendingAnalyticsEvents();
    if (pendingEvents.length > 0) {
      // Fire all queued events
      pendingEvents.forEach((event) => {
        window.gtag("event", event.event, event.params || {});
      });
      // Clear the cookie after firing all events
      clearPendingAnalyticsEvents();
    }
  }, [analyticsReady, cookieAccepted]);

  const onAccept = () => {
    setCookieAccepted(true);
    window.gtag("consent", "update", {
      analytics_storage: "granted",
    });
  };

  const onDecline = () => {
    setCookieAccepted(false);
    window.gtag("consent", "update", {
      analytics_storage: "denied",
    });
  };

  const onAnalyticsReady = () => {
    window.gtag("consent", "update", {
      analytics_storage: cookieAccepted ? "granted" : "denied",
    });
    setAnalyticsReady(true);
  };

  return (
    <>
      <Script
        strategy="afterInteractive"
        onReady={onAnalyticsReady}
        src={`https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`}
      ></Script>
      <Script id="google-gtag-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          
          // Default to denied until user gives consent
          gtag('consent', 'default', {
            'analytics_storage': 'denied'
          });

          gtag('config', '${GTAG_ID}', {
            page_path: window.location.pathname
          });
        `}
      </Script>
      {cookieAccepted && (
        <>
          <Script id="google-gtm" strategy="afterInteractive">{`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${GTM_ID}');
          `}</Script>
        </>
      )}
      <CookieConsent
        variant="default"
        onAcceptCallback={onAccept}
        onDeclineCallback={onDecline}
      />
    </>
  );
}
